import { RootStore } from "app/rootStore";
import { action, observable, makeObservable, runInAction, computed } from "mobx";
import { startLogin } from "shared/api/auth/authApi";
import { loginByCode, loginByToken } from "shared/api/auth/tokenApi";
import { requestAuthCode } from "shared/api/spaceStone/authApi";
import { AuthMethod } from "shared/constants/enums";
import { navigateOnExpiredOrErrorPage } from "shared/helpers";
import { SignInError } from "shared/interfaces/errors/SignInError";
import LoginOptions from "shared/models/loginOptions";
import IVerificationCodeResponse from "shared/models/verificationCodeResponse";
import routeNames from "shared/routes/constants/routeNames";
import { showSuccessToast } from "uiKit/container/toast/helpers";
import { pickServerValidationCode } from "shared/api/utils";
import { AxiosError } from "axios";

export enum LoginStep {
    EmailLoginForm,
    AuthMethodLoginForm,
    CheckInboxLoginForm,
    PhoneCodeForm,
}

export interface LoginState extends LoginOptions {
    activeStep: LoginStep;
    selectedAuthMethod: AuthMethod;
}

export function getDefaultLoginState(): LoginState {
    return {
        activeStep: LoginStep.EmailLoginForm,
        email: "",
        firstName: "",
        phone: "",
        selectedAuthMethod: AuthMethod.Email,
    };
}

const DEACTIVATED_USER_ERROR_CODE = "YouAreUnableToSignIn";

export class SignInStore {
    @observable public loginState: LoginState = getDefaultLoginState();
    @observable public errors: SignInError[] = [];
    @observable public deactivatedUserError = false;
    @observable public returnUrl: string | null = null;

    public rootStore: RootStore;

    constructor(rootStore: RootStore) {
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @computed
    public get hasErrors(): boolean {
        return this.errors.length > 0;
    }

    @action.bound
    public setReturnUrl(pathname: string): void {
        this.returnUrl = pathname;
    }

    @action.bound
    public resetReturnUrl(): void {
        this.returnUrl = null;
    }

    @action.bound
    public setEmail(email: string): void {
        this.loginState.email = email;
    }

    @action.bound
    setStep(step: LoginStep): void {
        this.loginState.activeStep = step;
    }

    @action.bound
    setSelectedAuthMethod(index: number): void {
        this.loginState.selectedAuthMethod = index;
    }

    @action.bound
    clearLoginState(): void {
        this.loginState = getDefaultLoginState();
    }

    @action.bound
    public clearErrors(): void {
        this.deactivatedUserError = false;
        this.errors = [];
    }

    @action.bound
    public clearStore(): void {
        this.clearLoginState();
        this.clearErrors();
    }

    @action.bound
    public async startLogin(email: string): Promise<void> {
        try {
            this.clearErrors();
            const startLoginModel = await startLogin(email);
            this.loginState = {
                ...this.loginState,
                ...startLoginModel,
                phone: startLoginModel.phone || "",
                firstName: startLoginModel.firstName || "",
            };

            if (this.loginState.phone && this.loginState.phone !== "") {
                this.setStep(LoginStep.AuthMethodLoginForm);
            } else {
                await this.requestLoginCodeForEmail();
            }
        } catch (err) {
            if (err instanceof Error && "isAxiosError" in err) {
                this.onLoginError(err);
            }
        }
    }

    @action.bound
    private onLoginError(error: AxiosError): void {
        const errorCode = pickServerValidationCode(error);
        if (errorCode === DEACTIVATED_USER_ERROR_CODE) {
            this.deactivatedUserError = true;
            return;
        }
        if (error.response && error.response.data && error.response.data.errors) {
            error.response.data.errors.forEach((e: SignInError) => {
                this.errors.push(e);
            });
        }
    }

    @action.bound
    public async requestLoginCode(toastMessage?: string): Promise<IVerificationCodeResponse | void> {
        try {
            this.clearErrors();
            const response = await requestAuthCode(
                this.loginState.email,
                this.loginState.selectedAuthMethod,
                this.returnUrl
            );

            if (toastMessage) {
                showSuccessToast({ message: toastMessage });
            }

            return response;
        } catch (error) {
            if (error instanceof Error && "isAxiosError" in error) {
                runInAction(() => this.errors.push(error as SignInError));
            }
        }
    }

    @action.bound
    public async requestLoginCodeForEmail(): Promise<void> {
        const response = await this.requestLoginCode();

        if (response && response.status === 200) {
            this.setStep(LoginStep.CheckInboxLoginForm);
        }
    }

    @action.bound
    public async requestLoginCodeForPhone(): Promise<void> {
        const response = await this.requestLoginCode();

        if (response && response.status === 200) {
            this.setStep(LoginStep.PhoneCodeForm);
        }
    }

    @action.bound
    async loginByCode(code: number): Promise<void> {
        try {
            this.clearErrors();
            const response = await loginByCode(this.loginState.email, code);
            this.rootStore.authStore.setAuthData(response);
            this.clearStore();
            this.resetReturnUrl();
        } catch (error) {
            if (error instanceof Error && "isAxiosError" in error) {
                runInAction(() => this.errors.push(error as SignInError));
            }
        }
    }

    @action.bound
    async loginByToken(token: string): Promise<void> {
        try {
            const response = await loginByToken(token);
            this.rootStore.authStore.setAuthData(response);
            this.clearStore();
            this.resetReturnUrl();
        } catch (error) {
            if (error instanceof Error && "isAxiosError" in error) {
                this.clearStore();
                navigateOnExpiredOrErrorPage(
                    error,
                    this.rootStore.routerStore,
                    routeNames.ERRORS.EXPIRED_CONF_LINK,
                    token,
                    this.returnUrl
                );
                runInAction(() => this.errors.push(error as SignInError));
            }
        }
    }

    private hasEmailAndPhone(): boolean {
        return this.loginState.email !== "" && this.loginState.phone !== "";
    }

    @action.bound
    public getBack(): void {
        this.setStep(this.hasEmailAndPhone() ? LoginStep.AuthMethodLoginForm : LoginStep.EmailLoginForm);
    }
}
